export const DateUtilService = {
    /**
     * Calculate the time between param timeStart and param timeEnd.
     * Return object with the current state of time.
     */
    getTimeMeasure(timeStartTimestamp, timeEndTimestamp) {
        const dateStart = new Date(timeStartTimestamp);
        const dateEnd = new Date(timeEndTimestamp);

        const difference = dateEnd - dateStart;
        let seconds = Math.floor((dateEnd - (dateStart)) / 1000);
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        hours -= (days * 24);
        minutes = minutes - (days * 24 * 60) - (hours * 60);
        seconds = seconds - (days * 24 * 60 * 60) - (hours * 60 * 60) - (minutes * 60);

        return {
            seconds,
            minutes,
            hours,
            days,
            difference,
        };
    },

    // region Parsers
    getDateTimeParts(timestamp) {
        const dateObject = new Date(timestamp);

        if (isNaN(dateObject)) {
            throw new Error('Wrong timestamp');
        }

        const year = dateObject.getFullYear();
        const month = dateObject.getMonth() + 1;
        const dayOfMonth = dateObject.getDate();
        const hours24format = dateObject.getHours();
        const hours12format = this.hours24to12(hours24format);
        const dayPeriod = this.getDayPeriod(hours24format);
        const minutes = dateObject.getMinutes();
        const seconds = dateObject.getSeconds();

        return {
            year,
            month,
            dayOfMonth,
            hours24format,
            hours12format,
            dayPeriod,
            minutes,
            seconds,
        };
    },

    getZeroLeadingDateTimeParts(timestamp) {
        let dateTimeParts;

        try {
            dateTimeParts = this.getDateTimeParts(timestamp);
        } catch (e) {
            Tools.error(e.message);
            return null;
        }

        const { year, dayPeriod } = dateTimeParts;
        let { month, dayOfMonth, hours24format, hours12format, minutes, seconds } = dateTimeParts;

        month = this.addLeadingZero(month);
        dayOfMonth = this.addLeadingZero(dayOfMonth);
        hours24format = this.addLeadingZero(hours24format);
        hours12format = this.addLeadingZero(hours12format);
        minutes = this.addLeadingZero(minutes);
        seconds = this.addLeadingZero(seconds);

        return {
            year,
            month,
            dayOfMonth,
            hours24format,
            hours12format,
            dayPeriod,
            minutes,
            seconds,
        };
    },

    formatDateTimeParts(isoStringOrDate, {
        locale = 'en-US',
        options = {},
    } = {}) {
        const date = new Date(isoStringOrDate);
        const defaultOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            dayPeriod: 'short',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        };
        const typesMapping = {
            day: 'dayOfMonth',
            minute: 'minutes',
            second: 'seconds',
        };
        const mergedOptions = {
            ...defaultOptions,
            ...options,
        };
        const dateTimeFormat = new Intl.DateTimeFormat(locale, mergedOptions);
        const parsedParts = dateTimeFormat.formatToParts(date);

        const parts = {};

        parsedParts.every(partPair => {
            let { type } = partPair;
            const { value } = partPair;

            if (Object.keys(typesMapping).includes(type)) {
                type = typesMapping[type];
            }

            if (type === 'hour') {
                parts['hours24format'] = value;
                parts['hours12format'] = this.hours24to12(value);
                parts['dayPeriod'] = this.getDayPeriod(value);
            } else if (type !== 'literal') {
                parts[type] = value;
            }

            return true;
        });

        return parts;
    },

    getDateTimePartsForTimeZone(isoStringOrDate, timeZone, {
        options = {},
        ...restConfig
    } = {}) {
        return DateUtilService.formatDateTimeParts(isoStringOrDate, {
            ...restConfig,
            options: {
                timeZone,
                ...options,
            },
        });
    },

    adjustDateWithOffset(date, offset) {
        // Parse the offset (e.g., "+05:30" or "-02:00")
        const offsetMatch = offset.match(/([+-])(\d{2}):?(\d{2})?/);
        if (!offsetMatch) {
            throw new Error('Invalid offset format. Expected "+HH:MM" or "-HH:MM".');
        }
    
        const sign = offsetMatch[1] === '+' ? 1 : -1;
        const hours = parseInt(offsetMatch[2], 10);
        const minutes = parseInt(offsetMatch[3] || '0', 10);
        const customOffsetMinutes = sign * (hours * 60 + minutes);
    
        // Adjust for the local time zone offset
        const localOffsetMinutes = -date.getTimezoneOffset(); // Local time offset from UTC in minutes
        const totalOffsetMinutes = customOffsetMinutes - localOffsetMinutes;
    
        // Adjust the date
        return new Date(date.getTime() + totalOffsetMinutes * 60 * 1000);
    },
    // endregion

    // region Helpers
    addLeadingZero(numberValue, maxLength = 2) {
        return numberValue.toString().padStart(maxLength, '0');
    },

    hours24to12(hours24format) {
        return hours24format % 12 || 12; // 0 is 12
    },

    getDayPeriod(hours24format) {
        return hours24format >= 12 ? 'pm' : 'am';
    },
    // endregion
};
